import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Mic, Send, RotateCw, Trophy, Sparkles, Lock, ChevronRight,
  CheckCircle2, AlertCircle, ListChecks, FileText, History,
} from "lucide-react";
import Footer from "@/components/Footer";
import VoiceRecorder from "@/components/VoiceRecorder";

const INDUSTRIES = ["Tech", "Finance", "Healthcare", "Retail", "Marketing", "Operations", "Education", "Generic"];

function ScoreBar({ label, value, testId }) {
  const color = value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono uppercase tracking-wider text-zinc-500">{label}</span>
        <span className="font-mono font-bold text-zinc-900">{value}</span>
      </div>
      <div className="h-1.5 bg-zinc-100">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ScoreCard({ score, question, category }) {
  if (!score) return null;
  return (
    <div className="bg-white border border-zinc-200 p-5 mb-4" data-testid="last-turn-score-card">
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">
          {category}
        </span>
        <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Last answer</span>
        <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-900 text-white text-xs font-mono">
          <Trophy className="w-3 h-3" /> {score.overall} / 100
        </span>
      </div>
      <p className="text-sm text-zinc-700 mb-4 line-clamp-2">{question}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <ScoreBar label="Clarity" value={score.clarity} testId="score-clarity" />
        <ScoreBar label="Relevance" value={score.relevance} testId="score-relevance" />
        <ScoreBar label="STAR" value={score.star_structure} testId="score-star" />
        <ScoreBar label="Confidence" value={score.confidence} testId="score-confidence" />
      </div>
      {(score.what_worked || []).length > 0 && (
        <div className="mb-3">
          <h4 className="text-[11px] font-mono uppercase tracking-wider text-emerald-700 mb-1.5 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> What worked
          </h4>
          <ul className="space-y-1 text-xs text-zinc-700">
            {score.what_worked.map((t, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">·</span>{t}</li>)}
          </ul>
        </div>
      )}
      {(score.what_to_improve || []).length > 0 && (
        <div className="mb-3">
          <h4 className="text-[11px] font-mono uppercase tracking-wider text-rose-700 mb-1.5 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> What to improve
          </h4>
          <ul className="space-y-1 text-xs text-zinc-700">
            {score.what_to_improve.map((t, i) => <li key={i} className="flex gap-2"><span className="text-rose-600">·</span>{t}</li>)}
          </ul>
        </div>
      )}
      {score.model_answer_outline && (
        <div className="pt-3 border-t border-zinc-100">
          <h4 className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Top-candidate outline</h4>
          <p className="text-xs text-zinc-600 italic">{score.model_answer_outline}</p>
        </div>
      )}
    </div>
  );
}

export default function MockInterview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [quota, setQuota] = useState(null);
  const [history, setHistory] = useState([]);
  const [phase, setPhase] = useState("setup"); // setup | active | finished
  const [session, setSession] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [lastScore, setLastScore] = useState(null);
  const [lastScoreQ, setLastScoreQ] = useState({ q: "", c: "" });
  const [submitting, setSubmitting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  // Setup form state
  const [role, setRole] = useState("");
  const [industry, setIndustry] = useState(searchParams.get("role") || "Generic");
  const [jobDescription, setJobDescription] = useState("");
  const [nQuestions, setNQuestions] = useState(5);
  const [useResume, setUseResume] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ownedPacks, setOwnedPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(searchParams.get("pack") || "");

  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: "/interview/mock" } });
      return;
    }
    refreshQuota();
    refreshHistory();
    refreshOwnedPacks();
    // eslint-disable-next-line
  }, [user]);

  const refreshQuota = async () => {
    try {
      const { data } = await api.get("/interview/mock/quota");
      setQuota(data);
    } catch (e) { /* noop */ }
  };
  const refreshHistory = async () => {
    try {
      const { data } = await api.get("/interview/mock/history/list");
      setHistory(data.sessions || []);
    } catch (e) { /* noop */ }
  };
  const refreshOwnedPacks = async () => {
    try {
      const { data } = await api.get("/packs/me/owned");
      setOwnedPacks(data.items || []);
    } catch (e) { /* noop */ }
  };

  const currentTurnIndex = session ? (session.turns || []).length : 0;
  const currentQuestion = useMemo(() => {
    if (!session) return null;
    const qs = session.questions || [];
    return qs[currentTurnIndex] || null;
  }, [session, currentTurnIndex]);

  const handleStart = async (e) => {
    e?.preventDefault();
    if (!role.trim()) {
      toast.error("Please enter the role you're interviewing for.");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/interview/mock/start", {
        role: role.trim(),
        industry: industry !== "Generic" ? industry : undefined,
        job_description: jobDescription.trim() || undefined,
        use_my_resume: useResume,
        n_questions: nQuestions,
        pack_slug: selectedPack || undefined,
      });
      setSession(data);
      setPhase("active");
      setLastScore(null);
      setCurrentAnswer("");
      toast.success("Mock interview started — good luck!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to start mock.";
      toast.error(msg);
      if (err?.response?.status === 402) {
        // out of quota — refresh and offer upgrade
        refreshQuota();
      }
    } finally {
      setStarting(false);
    }
  };

  const handleSubmit = async (answerText, source = "text") => {
    if (!session || !currentQuestion) return;
    const text = (answerText ?? currentAnswer).trim();
    if (text.length < 10) {
      toast.error("Your answer is too short — try at least a sentence or two.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/interview/mock/${session.session_id}/answer`, {
        answer: text,
        answer_source: source,
      });
      setLastScore(data.score);
      setLastScoreQ({ q: currentQuestion.question, c: currentQuestion.category });
      setCurrentAnswer("");
      // refetch session for updated turns + status
      const { data: fresh } = await api.get(`/interview/mock/${session.session_id}`);
      setSession(fresh);
      if (data.is_finished) {
        setPhase("finished");
        refreshHistory();
        toast.success("Mock interview complete!");
      } else {
        toast.success(`Answer scored: ${data.score.overall}/100`);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to score answer.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoiceComplete = async (blob) => {
    if (!session) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("file", blob, "answer.webm");
      const { data } = await api.post(`/interview/mock/${session.session_id}/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data.text || "").trim();
      if (!text || text.length < 5) {
        toast.error("We couldn't make out your answer. Try recording again.");
        return;
      }
      setCurrentAnswer(text);
      toast.success("Transcribed — review the text and submit.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transcription failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const handleFinishEarly = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/interview/mock/${session.session_id}/finish`);
      setSession(data);
      setPhase("finished");
      refreshHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't finish session.");
    }
  };

  const handleStartNew = () => {
    setSession(null);
    setLastScore(null);
    setCurrentAnswer("");
    setRole("");
    setJobDescription("");
    setPhase("setup");
    refreshQuota();
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50/40 via-fuchsia-50/30 to-amber-50/40" data-testid="mock-interview-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">

        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-6 h-6 bg-zinc-900 text-white">
              <Mic className="w-3.5 h-3.5" strokeWidth={2.25} />
            </span>
            <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Live Mock Interview</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.02] text-zinc-900">
                Practice with an AI<br /><span className="text-[#DC1C22]">that grades you live.</span>
              </h1>
              <p className="mt-4 text-zinc-600 max-w-xl">
                Pick your role, answer in text or voice, and get scored on Clarity, Relevance, STAR-method, and Confidence after every question.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              {quota && (
                <div className="inline-flex flex-col items-start lg:items-end gap-1" data-testid="mock-quota">
                  {quota.is_pro ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#DC1C22] text-white text-[10px] font-mono uppercase tracking-widest">
                      <Sparkles className="w-3 h-3" /> Premium · unlimited mocks
                    </span>
                  ) : (
                    <>
                      <span className="text-sm text-zinc-700">
                        <span className="font-bold text-zinc-900">{quota.used_this_month}</span>
                        <span className="text-zinc-500"> / {quota.free_limit} free mocks this month</span>
                      </span>
                      <Link to="/pricing" className="text-xs text-[#DC1C22] underline-offset-4 hover:underline">
                        Need more? Upgrade to Premium →
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SETUP PHASE */}
        {phase === "setup" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <form
              onSubmit={handleStart}
              data-testid="mock-setup-form"
              className="lg:col-span-8 bg-white border border-zinc-200 p-6 lg:p-8 space-y-5"
            >
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">
                  Target role <span className="text-[#DC1C22]">*</span>
                </label>
                <input
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g. Senior Backend Engineer, Marketing Manager…"
                  data-testid="mock-role-input"
                  className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">Industry</label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    data-testid="mock-industry-select"
                    className="w-full px-3 py-2.5 border border-zinc-200 text-sm bg-white focus:outline-none focus:border-zinc-900"
                  >
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">Number of questions</label>
                  <select
                    value={nQuestions}
                    onChange={(e) => setNQuestions(Number(e.target.value))}
                    data-testid="mock-n-questions"
                    className="w-full px-3 py-2.5 border border-zinc-200 text-sm bg-white focus:outline-none focus:border-zinc-900"
                  >
                    {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} questions (~{n * 2} min)</option>)}
                  </select>
                </div>
              </div>
              {/* Booster pack selector */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-zinc-700 mb-0.5">
                      Use a Booster Pack <span className="text-zinc-400">(optional)</span>
                    </label>
                    <p className="text-[11px] text-zinc-500">
                      Industry-curated questions instead of AI-generated.
                    </p>
                  </div>
                  <Link
                    to="/interview/packs"
                    data-testid="mock-browse-packs-link"
                    className="text-[11px] font-mono uppercase tracking-wider text-[#DC1C22] hover:underline underline-offset-4 shrink-0"
                  >
                    Browse all →
                  </Link>
                </div>
                {ownedPacks.length === 0 ? (
                  <p className="text-xs text-zinc-600">
                    You don&apos;t own any packs yet. <Link to="/interview/packs" className="text-[#DC1C22] underline-offset-4 hover:underline">Explore industry packs</Link> — $14–$24 one-time.
                  </p>
                ) : (
                  <select
                    value={selectedPack}
                    onChange={(e) => setSelectedPack(e.target.value)}
                    data-testid="mock-pack-select"
                    className="w-full px-3 py-2.5 border border-amber-300 text-sm bg-white focus:outline-none focus:border-zinc-900"
                  >
                    <option value="">— AI-generated questions (default) —</option>
                    {ownedPacks.map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.name} ({p.n_questions} Qs available)
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1.5">
                  Paste the job description <span className="text-zinc-400 normal-case">(optional, sharper questions)</span>
                </label>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  rows={4}
                  placeholder="Paste the JD — we'll tailor questions to it."
                  data-testid="mock-jd-input"
                  className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700">
                <input
                  type="checkbox"
                  checked={useResume}
                  onChange={(e) => setUseResume(e.target.checked)}
                  data-testid="mock-use-resume"
                  className="mt-1"
                />
                <span>
                  Reference my latest saved resume to personalize questions
                  <span className="block text-xs text-zinc-500 mt-0.5">
                    We&apos;ll pull from your most recent Builder draft, if any. No upload needed.
                  </span>
                </span>
              </label>
              <div className="flex items-center gap-3 pt-2 border-t border-zinc-100">
                <button
                  type="submit"
                  disabled={starting || (quota && !quota.can_start)}
                  data-testid="mock-start-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#DC1C22] hover:bg-[#A8141A] text-white text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {starting ? <RotateCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {starting ? "Generating questions…" : "Start mock interview"}
                </button>
                {quota && !quota.can_start && (
                  <Link to="/pricing" className="text-sm text-[#DC1C22] inline-flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5" /> Out of free mocks · upgrade
                  </Link>
                )}
              </div>
            </form>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-zinc-200 p-5">
                <h3 className="font-display font-bold text-zinc-900 mb-3 flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-[#DC1C22]" /> What you&apos;ll get
                </h3>
                <ul className="space-y-2 text-sm text-zinc-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> 3–8 tailored questions</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Live AI scoring per answer</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> What worked + what to improve</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Top-candidate answer outline</li>
                  {!quota?.is_pro && (
                    <li className="flex gap-2"><Lock className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" /> <span className="text-zinc-500">Voice answers · <Link to="/pricing" className="text-[#DC1C22] underline-offset-4 hover:underline">Premium</Link></span></li>
                  )}
                </ul>
              </div>
              {history.length > 0 && (
                <div className="bg-white border border-zinc-200 p-5">
                  <h3 className="font-display font-bold text-zinc-900 mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-zinc-500" /> Recent mocks
                  </h3>
                  <ul className="space-y-3">
                    {history.slice(0, 5).map((h) => (
                      <li key={h.session_id} className="text-sm border-b border-zinc-100 pb-2 last:border-b-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-zinc-900 truncate">{h.role || "Untitled"}</span>
                          {h.overall != null && (
                            <span className="font-mono text-xs font-bold text-zinc-900">{h.overall}/100</span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 flex items-center gap-2">
                          <span>{new Date(h.created_at).toLocaleDateString()}</span>
                          <span>·</span>
                          <span>{h.n_questions} Qs</span>
                          <span className={`ml-auto px-1.5 py-0.5 text-[9px] font-mono uppercase ${h.status === "finished" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {h.status === "finished" ? "Done" : "Open"}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="bg-zinc-50 border border-zinc-200 p-5">
                <h3 className="font-display font-bold text-zinc-900 mb-2">First time interviewing?</h3>
                <p className="text-sm text-zinc-600 mb-3">Browse the free Prep Library to see model answers + STAR-method tips for 24+ classic questions.</p>
                <Link
                  to="/interview/prep"
                  data-testid="mock-to-prep-link"
                  className="inline-flex items-center gap-1.5 text-sm text-[#DC1C22] font-medium hover:underline underline-offset-4"
                >
                  Open Prep Library <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </aside>
          </div>
        )}

        {/* ACTIVE PHASE */}
        {phase === "active" && session && currentQuestion && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="mock-active-phase">
            <div className="lg:col-span-8 space-y-4">
              {lastScore && <ScoreCard score={lastScore} question={lastScoreQ.q} category={lastScoreQ.c} />}
              <div className="bg-white border border-zinc-200 p-6 lg:p-7">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">
                    Q {currentTurnIndex + 1} / {(session.questions || []).length}
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-900 text-white">
                    {currentQuestion.category}
                  </span>
                </div>
                <h2 className="font-display font-bold text-2xl lg:text-3xl text-zinc-900 leading-snug mb-4">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.rationale && (
                  <p className="text-xs text-zinc-500 mb-5 italic">{currentQuestion.rationale}</p>
                )}
                <textarea
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  rows={7}
                  placeholder="Type your answer here, or use the voice recorder below (Premium)…"
                  data-testid="mock-answer-input"
                  disabled={submitting || transcribing}
                  className="w-full px-4 py-3 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
                />
                <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={submitting || transcribing || currentAnswer.trim().length < 10}
                      onClick={() => handleSubmit()}
                      data-testid="mock-submit-answer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#DC1C22] hover:bg-[#A8141A] text-white text-sm font-medium disabled:opacity-40 transition-colors"
                    >
                      {submitting ? <RotateCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {submitting ? "Scoring…" : "Submit answer"}
                    </button>
                    <span className="text-xs text-zinc-500 font-mono">
                      {currentAnswer.trim().split(/\s+/).filter(Boolean).length} words
                    </span>
                  </div>
                  {currentTurnIndex >= 3 && (
                    <button
                      type="button"
                      onClick={handleFinishEarly}
                      data-testid="mock-finish-early"
                      className="text-xs text-zinc-500 underline-offset-4 hover:underline"
                    >
                      Finish early & see results
                    </button>
                  )}
                </div>
                {user?.is_pro ? (
                  <div className="mt-5 pt-5 border-t border-zinc-100">
                    {transcribing ? (
                      <div className="text-sm text-zinc-600 inline-flex items-center gap-2">
                        <RotateCw className="w-4 h-4 animate-spin" /> Transcribing…
                      </div>
                    ) : (
                      <VoiceRecorder
                        maxSeconds={90}
                        onComplete={handleVoiceComplete}
                        onError={(m) => toast.error(m)}
                        disabled={submitting}
                      />
                    )}
                  </div>
                ) : (
                  <div className="mt-5 pt-5 border-t border-zinc-100">
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Lock className="w-3.5 h-3.5" />
                      <Link to="/pricing" className="text-[#DC1C22] underline-offset-4 hover:underline">Upgrade to Premium</Link>
                      <span>to answer with voice (Whisper-transcribed).</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress sidebar */}
            <aside className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-zinc-200 p-5">
                <h3 className="font-display font-bold text-zinc-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#DC1C22]" /> Your mock
                </h3>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-zinc-500">Role</dt><dd className="font-medium text-zinc-900 truncate ml-3 max-w-[60%] text-right">{session.role}</dd></div>
                  {session.industry && <div className="flex justify-between"><dt className="text-zinc-500">Industry</dt><dd className="font-medium text-zinc-900">{session.industry}</dd></div>}
                  <div className="flex justify-between"><dt className="text-zinc-500">Progress</dt><dd className="font-mono text-zinc-900">{currentTurnIndex} / {(session.questions || []).length}</dd></div>
                </dl>
              </div>
              <div className="bg-white border border-zinc-200 p-5">
                <h3 className="font-display font-bold text-zinc-900 mb-3">Upcoming questions</h3>
                <ol className="space-y-2 text-sm">
                  {(session.questions || []).map((q, i) => {
                    const done = i < currentTurnIndex;
                    const active = i === currentTurnIndex;
                    return (
                      <li
                        key={i}
                        data-testid={`upcoming-q-${i}`}
                        className={`flex gap-2 items-start ${done ? "text-zinc-400 line-through" : active ? "text-zinc-900 font-medium" : "text-zinc-600"}`}
                      >
                        <span className="font-mono text-xs mt-0.5">{i + 1}.</span>
                        <span className="text-xs leading-snug">{done ? q.question : (active ? q.question : "·  ·  ·  ·  ·")}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </aside>
          </div>
        )}

        {/* FINISHED PHASE */}
        {phase === "finished" && session && (
          <div className="space-y-6" data-testid="mock-finished-phase">
            <div className="bg-white border border-zinc-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Trophy className="w-8 h-8 text-[#DC1C22]" />
                <div>
                  <h2 className="font-display font-extrabold text-3xl text-zinc-900">Interview complete</h2>
                  <p className="text-sm text-zinc-500">{session.role}{session.industry ? ` · ${session.industry}` : ""}</p>
                </div>
                <div className="ml-auto text-right">
                  <div className="font-display font-extrabold text-5xl text-zinc-900" data-testid="final-overall">
                    {session.summary?.overall || 0}
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Overall</div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-5">
                <ScoreBar label="Clarity" value={session.summary?.clarity || 0} testId="final-clarity" />
                <ScoreBar label="Relevance" value={session.summary?.relevance || 0} testId="final-relevance" />
                <ScoreBar label="STAR" value={session.summary?.star_structure || 0} testId="final-star" />
                <ScoreBar label="Confidence" value={session.summary?.confidence || 0} testId="final-confidence" />
              </div>
            </div>

            <div className="bg-white border border-zinc-200 p-6 lg:p-8">
              <h3 className="font-display font-bold text-xl text-zinc-900 mb-4 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-[#DC1C22]" /> Full transcript
              </h3>
              <ol className="space-y-6">
                {(session.turns || []).map((t, i) => (
                  <li key={i} data-testid={`transcript-turn-${i}`} className="pb-6 border-b border-zinc-100 last:border-b-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-900 text-white">Q{i + 1}</span>
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">{t.category}</span>
                      <span className="ml-auto font-mono text-xs font-bold text-zinc-900">{t.score?.overall ?? 0}/100</span>
                    </div>
                    <p className="font-display font-semibold text-zinc-900 mb-2">{t.question}</p>
                    <p className="text-sm text-zinc-700 bg-zinc-50 p-3 border border-zinc-100 whitespace-pre-wrap mb-3">
                      {t.answer}
                    </p>
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      <ScoreBar label="Clarity" value={t.score?.clarity || 0} />
                      <ScoreBar label="Relevance" value={t.score?.relevance || 0} />
                      <ScoreBar label="STAR" value={t.score?.star_structure || 0} />
                      <ScoreBar label="Confidence" value={t.score?.confidence || 0} />
                    </div>
                    {t.score?.model_answer_outline && (
                      <p className="text-xs text-zinc-600 italic border-l-2 border-zinc-300 pl-3 mt-2">
                        <span className="not-italic font-mono uppercase tracking-wider text-[10px] text-zinc-500">Top-candidate outline · </span>
                        {t.score.model_answer_outline}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleStartNew}
                data-testid="mock-start-new"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DC1C22] hover:bg-[#A8141A] text-white text-sm font-medium transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Start another mock
              </button>
              <Link to="/interview/prep" className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900">
                Browse Prep Library <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/builder" className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900">
                Polish my resume <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
